import {
  type SupportChatEvent,
  type SupportChatProvider,
  beaconSupportChatEvent,
} from "@/api/supportChat"
import {
  hidePylon,
  isPylonLoaded,
  showNewPylonMessage,
  showPylon,
  subscribePylonUnreadCount,
  subscribePylonVisibility,
} from "@/util/pylon"
import { logEvent } from "@/util/telemetry"
import { viewManager } from "@/util/viewManager"

export interface SupportChatVisibilityHandlers {
  onShow: () => void
  onHide: () => void
}

// Pylon is the only in-app support provider. Whether a widget renders is
// decided server-side (gate + Pylon config); the backend reflects that in the
// `support_chat_provider` view-data flag (see pack_view_data.rb). When it is
// absent — no Pylon config, or neither Pylon gate on — there is no chat widget,
// so support entry points (the nav "Live chat" button, contact-support CTAs)
// stay hidden or fall back instead of invoking a dead widget.
export function isSupportChatAvailable(): boolean {
  return viewManager.get("supportChatProvider") === "pylon"
}

// The provider whose outcomes are reported to the telemetry beacon. Only Pylon
// is supported, but the counter keeps a provider label for historical
// continuity and future providers.
const SUPPORT_CHAT_PROVIDER: SupportChatProvider = "pylon"

// A logged-in dashboard user with Pylon configured should always end up with a
// working chat widget, but the widget script loads asynchronously and can be
// blocked (CSP, ad blockers, missing config). Poll for it so we either confirm
// it resolved or, after a grace period, record a failure — guaranteeing every
// session that expects a widget reports exactly one outcome instead of failing
// silently.
const RESOLUTION_TIMEOUT_MS = 15_000
const RESOLUTION_POLL_INTERVAL_MS = 500

async function beaconSupportChatEventWithLog(
  provider: SupportChatProvider,
  event: SupportChatEvent,
): Promise<void> {
  const result = await beaconSupportChatEvent(provider, event)
  if (result.isErr) {
    const { message, status, code } = result.error
    logEvent("error", "Failed to beacon support chat event", {
      sentryContext: "SupportChat",
      fingerprint: ["support-chat-beacon-failed", provider, event],
      extra: { error: { message, status, code } },
    })
  }
}

function captureSupportChatResolutionFailure(elapsedMs: number): void {
  logEvent("error", `Support chat failed to resolve (${SUPPORT_CHAT_PROVIDER})`, {
    sentryContext: "SupportChat",
    fingerprint: ["support-chat-unresolved", SUPPORT_CHAT_PROVIDER],
    extra: {
      pylonLoaded: isPylonLoaded(),
      elapsedMs,
    },
  })
}

// Begins watching for the Pylon widget to load and reports the single
// resolved/failed outcome. Safe to call once per authenticated session. Only
// reports when a widget is actually expected (`isSupportChatAvailable`); embed
// iframes and instances without Pylon config have no widget and must not be
// counted as failures.
export function resolveSupportChat(): void {
  if (process.env.NODE_ENV === "development") {
    return
  }

  if (!isSupportChatAvailable()) {
    return
  }

  const startedAt = Date.now()

  const poll = (): void => {
    // Check load before the deadline: a widget that boots in the final polling
    // interval is working and must report "resolved", not a false "failed".
    // Polling stops once the deadline passes, so this can't report "resolved"
    // for a widget that loads well after the budget.
    if (isPylonLoaded()) {
      void beaconSupportChatEventWithLog(SUPPORT_CHAT_PROVIDER, "resolved")
      return
    }

    const elapsedMs = Date.now() - startedAt
    if (elapsedMs >= RESOLUTION_TIMEOUT_MS) {
      captureSupportChatResolutionFailure(elapsedMs)
      void beaconSupportChatEventWithLog(SUPPORT_CHAT_PROVIDER, "failed")
      return
    }

    window.setTimeout(poll, RESOLUTION_POLL_INTERVAL_MS)
  }

  poll()
}

// Records that the user actually opened the chat composer, so usage can be
// distinguished from a widget that merely loaded.
export function reportSupportChatOpened(): void {
  if (!isSupportChatAvailable()) {
    return
  }
  void beaconSupportChatEventWithLog(SUPPORT_CHAT_PROVIDER, "opened")
}

// Pylon is configured for this session but `window.Pylon` is absent, so a
// support CTA silently does nothing. Since the widget was expected, this means
// it never loaded — typically a blocked widget script. Capture it instead of
// leaving the button dead. The fingerprint groups every occurrence into one
// Sentry issue rather than paging per click.
function captureWidgetUnavailable(): void {
  logEvent("error", "Pylon configured but widget unavailable on support CTA click", {
    sentryContext: "Pylon",
    fingerprint: ["pylon-widget-unavailable"],
  })
}

// Opens the support chat with a prepopulated message. Returns whether the chat
// widget handled the request, preserving the boolean contract callers rely on
// to fall back (e.g. to a pricing link / mailto).
export function showNewSupportMessage(message?: string): boolean {
  if (!isSupportChatAvailable()) {
    return false
  }
  const handled = showNewPylonMessage(message)
  if (!handled) {
    captureWidgetUnavailable()
  }
  return handled
}

// Opens the support chat window without prepopulating a message.
export function showSupportChat(): boolean {
  if (!isSupportChatAvailable()) {
    return false
  }
  const handled = showPylon()
  if (!handled) {
    captureWidgetUnavailable()
  }
  return handled
}

// Closes the support chat window.
export function hideSupportChat(): boolean {
  if (!isSupportChatAvailable()) {
    return false
  }
  return hidePylon()
}

// Subscribes to open/close events from the widget so callers can reflect its
// state. Returns a cleanup that unregisters the handlers.
export function subscribeSupportChatVisibility(
  handlers: SupportChatVisibilityHandlers,
): () => void {
  if (!isSupportChatAvailable()) {
    return () => {}
  }
  return subscribePylonVisibility(handlers)
}

// Subscribes to unread-count changes from the widget so callers can surface an
// unread badge. Returns a cleanup that unregisters the handler.
export function subscribeSupportChatUnreadCount(
  handler: (count: number) => void,
): () => void {
  if (!isSupportChatAvailable()) {
    return () => {}
  }
  return subscribePylonUnreadCount(handler)
}
